'use client'

import { CardContent } from 'ui/card'
import AvatarCustom from 'components/AvatarCustom'
import { Date_get24hHHMM, Date_getYYYYMMDD } from 'utils/date'
import { NormalizedMatch, EaMatch, detectExtraTimeAndPenalties } from 'lib/proclubs/shared'
import { Badge } from 'ui/badge'

interface ProclubsMatchCardProps {
    match: NormalizedMatch
    clubId: string
    onClick: () => void
    rawMatch?: EaMatch | null
}

const ProclubsMatchCard = ({ match, clubId, onClick, rawMatch }: ProclubsMatchCardProps) => {
    // Determine home and away teams based on clubId
    const isHome = match.clubId === clubId
    const homeClubId = isHome ? match.clubId : match.opponentClubId
    const awayClubId = isHome ? match.opponentClubId : match.clubId
    const homeClubName = isHome ? match.clubName : match.opponentName
    const awayClubName = isHome ? match.opponentName : match.clubName
    const homeScore = isHome ? match.goalsFor : match.goalsAgainst
    const awayScore = isHome ? match.goalsAgainst : match.goalsFor

    // Ensure playedAt is a Date object (it may be serialized as a string from the API)
    const playedAtDate = match.playedAt instanceof Date 
        ? match.playedAt 
        : new Date(match.playedAt)

    // Detect extra time and penalties
    const { hasExtraTime, wonOnPenalties } = detectExtraTimeAndPenalties(match, rawMatch || null)

    return (
        <CardContent className='relative grid mb-0 cursor-pointer p-3 md:p-6' onClick={onClick}>
            <div className='!w-full mx-auto grid items-center text-center pt-2 md:pt-4'>
                {/* Teams and Scores */}
                <div className='grid grid-cols-[1fr_auto_1fr] items-center !w-full gap-2 md:gap-5 lg:gap-8'>
                    {/* Home Team */}
                    <div className='grid items-center gap-1 md:gap-2 min-w-0'>
                        <div className='flex justify-center'>
                            <AvatarCustom
                                className='mx-auto md:!w-[90px] md:!h-[90px] !size-12 sm:!size-16'
                                avatarURL=''
                            >
                                {homeClubName || homeClubId}
                            </AvatarCustom>
                        </div>
                        <div className='text-xs sm:text-sm truncate px-1 md:px-2'>
                            {homeClubName || homeClubId}
                        </div>
                    </div>

                    {/* Scores and Date */}
                    <div className='grid items-center flex-shrink-0 w-[120px] sm:w-[140px] md:w-[160px]'>
                        <h6 className='text-xs sm:text-sm md:text-md mb-2 md:mb-4 whitespace-nowrap'>
                            {Date_getYYYYMMDD(playedAtDate)}
                        </h6>

                        <div className='flex items-center justify-center gap-2 md:gap-4'>
                            <div
                                className={`font-bold text-lg sm:text-xl md:text-2xl ${
                                    match.isWin && isHome
                                        ? 'text-green-600'
                                        : match.isLoss && isHome
                                        ? 'text-red-600'
                                        : match.isWin && !isHome
                                        ? 'text-red-600'
                                        : match.isLoss && !isHome
                                        ? 'text-green-600'
                                        : 'text-gray-500'
                                }`}
                            >
                                {homeScore}
                            </div>
                            <h5 className='text-gray-500 font-bold text-xs sm:text-sm md:text-base'>VS</h5>
                            <div
                                className={`font-bold text-lg sm:text-xl md:text-2xl ${
                                    match.isWin && !isHome
                                        ? 'text-green-600'
                                        : match.isLoss && !isHome
                                        ? 'text-red-600'
                                        : match.isWin && isHome
                                        ? 'text-red-600'
                                        : match.isLoss && isHome
                                        ? 'text-green-600'
                                        : 'text-gray-500'
                                }`}
                            >
                                {awayScore}
                            </div>
                        </div>
                        {/* Extra Time and Penalties Indicators */}
                        {(hasExtraTime || wonOnPenalties) && (
                            <div className='flex items-center justify-center gap-2 mt-1'>
                                {hasExtraTime && (
                                    <Badge variant='outline' className='text-xs'>
                                        ET
                                    </Badge>
                                )}
                                {wonOnPenalties && (
                                    <Badge variant='outline' className='text-xs'>
                                        Won on pens
                                    </Badge>
                                )}
                            </div>
                        )}
                        <h6 className='text-xs sm:text-sm md:text-base lg:text-lg mt-1 md:mt-2 whitespace-nowrap'>
                            {Date_get24hHHMM(playedAtDate)}
                        </h6>
                    </div>

                    {/* Away Team */}
                    <div className='grid items-center gap-1 md:gap-2 min-w-0'>
                        <div className='flex justify-center'>
                            <AvatarCustom
                                className='mx-auto md:!w-[90px] md:!h-[90px] !size-12 sm:!size-16'
                                avatarURL=''
                            >
                                {awayClubName || awayClubId}
                            </AvatarCustom>
                        </div>
                        <div className='text-xs sm:text-sm truncate px-1 md:px-2'>
                            {awayClubName || awayClubId}
                        </div>
                    </div>
                </div>
            </div>
        </CardContent>
    )
}

export default ProclubsMatchCard

